/**
 * ats.js — mandatory freeze-pack enforcement ("Meal Guard Freeze Pack")
 *
 * Rewritten Jul 2026 (P1 cart-request fix):
 * The previous version intercepted every cart/add form submit and re-ran the
 * whole add sequence itself (add -> cart.js -> add freeze -> cart?sections).
 * Together with a duplicate script include and the theme's own product-form
 * handler, one add-to-cart fired ~9 cart requests with overlapping writes,
 * causing HTTP 409 ("cart locked") failures and failed cart updates.
 *
 * This version never adds the user's item — the calling script owns that
 * (product-form.js, global.js quick-add, Boost quick-view, custom.js). It
 * only tops up the freeze pack AFTER a successful add, strictly one request
 * at a time, so cart writes never overlap.
 */
(function () {
  'use strict';

  // Immunity against double inclusion (the original bug class).
  if (window.__atsCartFixApplied) return;
  window.__atsCartFixApplied = true;

  var PRODUCT_MUST_ID = 40914461425700; // Meal Guard Freeze Pack variant
  var FREEZE_MARKER = 'X-ATS-Freeze';
  var ensureInFlight = false;

  /* --------------------------------------------------------------------
   * Freeze-pack top-up: runs after a successful cart add. Adds the freeze
   * pack only when the cart holds at least one other non-gift-card item
   * and no freeze pack yet. No re-render needed afterwards — the freeze
   * line is hidden in the drawer and excluded from all counts (Liquid).
   * ------------------------------------------------------------------ */
  function ensureFreezePack() {
    if (ensureInFlight) return;
    ensureInFlight = true;

    fetch('/cart.js')
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js responded ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        var hasFreeze = cart.items.some(function (item) {
          return item.variant_id === PRODUCT_MUST_ID;
        });
        // Gift cards need no cooling — never force the freeze pack into a
        // gift-card-only cart (matches pre-rewrite behavior).
        var hasOther = cart.items.some(function (item) {
          return item.variant_id !== PRODUCT_MUST_ID && !item.gift_card;
        });
        if (hasFreeze || !hasOther) return;

        var headers = { 'Content-Type': 'application/json' };
        headers[FREEZE_MARKER] = '1';

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            items: [
              { id: PRODUCT_MUST_ID, quantity: 1, properties: { _hidden: 'true' } }
            ]
          })
        });
      })
      .catch(function (err) {
        // Never throw into the page (e.g. bot-challenge HTML instead of JSON).
        console.warn('[ats] freeze-pack check skipped:', err);
      })
      .finally(function () {
        ensureInFlight = false;
      });
  }

  /* --------------------------------------------------------------------
   * Passive post-add observer on window.fetch. Watches every JS add path
   * (product-form.js and global.js post to /cart/add; Boost, custom.js and
   * the gift-card block post to /cart/add.js). Never alters the original
   * request or its promise.
   * ------------------------------------------------------------------ */
  function isCartAddUrl(input) {
    try {
      var url = typeof input === 'string' ? input : input && input.url;
      if (!url) return false;
      var path = new URL(url, window.location.origin).pathname;
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function isOwnFreezeAdd(input, init) {
    try {
      var headers = (init && init.headers) || (input && input.headers);
      if (!headers) return false;
      if (typeof headers.get === 'function') return !!headers.get(FREEZE_MARKER);
      return !!headers[FREEZE_MARKER];
    } catch (e) {
      return false;
    }
  }

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var result = originalFetch.apply(this, arguments);
    try {
      if (isCartAddUrl(input) && !isOwnFreezeAdd(input, init)) {
        // Separate consumer of the same promise: the caller's response,
        // error handling and timing stay exactly as they were.
        result
          .then(function (response) {
            if (response && response.ok) ensureFreezePack();
          })
          .catch(function () { /* caller handles its own errors */ });
      }
    } catch (e) { /* observer must never break the page */ }
    return result;
  };

  /* --------------------------------------------------------------------
   * Page-load safety net: covers carts created without a fetch add (e.g.
   * marketing cart permalinks) and heals older freeze-less carts. Runs
   * once per page, only when the header bubble shows items.
   * Removable if the extra /cart.js read per page view is unwanted.
   * ------------------------------------------------------------------ */
  function safetyNet() {
    try {
      var bubble = document.querySelector('.cart-count-bubble');
      var count = bubble ? parseInt(bubble.textContent, 10) : 0;
      if (count > 0) ensureFreezePack();
    } catch (e) { /* never break the page */ }
  }
  if (document.readyState === 'complete') {
    setTimeout(safetyNet, 1500);
  } else {
    window.addEventListener('load', function () {
      setTimeout(safetyNet, 1500);
    });
  }

  /* --------------------------------------------------------------------
   * Kept from the previous version: block removing the freeze pack via
   * the drawer/cart JS (CartDrawerItems inherits this method).
   * ------------------------------------------------------------------ */
  customElements.whenDefined('cart-items').then(function () {
    var originalUpdateQuantity = CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = async function (line, quantity, name) {
      // Only intercept removals (quantity set to 0)
      if (parseInt(quantity, 10) === 0) {
        var cart = await fetch('/cart.js').then(function (res) { return res.json(); });
        var item = cart.items[line - 1]; // line is a 1-based index

        if (item && item.variant_id === PRODUCT_MUST_ID) {
          return; // required item — block the removal
        }
      }
      return originalUpdateQuantity.call(this, line, quantity, name);
    };
  });

  /* --------------------------------------------------------------------
   * Kept from the previous version: belt-and-braces styling that greys
   * out / hides the freeze-pack remove control wherever it renders.
   * ------------------------------------------------------------------ */
  var customStyle = document.createElement('style');
  customStyle.innerHTML =
    'cart-remove-button button[aria-label="Remove Meal Guard Freeze Pack"] {' +
    '  opacity: 0.5;' +
    '  cursor: not-allowed;' +
    '}' +
    'quantity-input:has(+ cart-remove-button button[aria-label="Remove Meal Guard Freeze Pack"]) {' +
    '  display: none !important;' +
    '}';
  document.head.appendChild(customStyle);
})();
